import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

const Stacked = ({ top, bottom, topBold = true, topColor, bottomColor, mutedColor }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topBold ? 700 : 500, fontSize: '12px', color: topColor || '#0f172a' }}>
      {top || '—'}
    </div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: bottomColor || mutedColor || '#64748b', marginTop: '1px' }}>
        {bottom}
      </div>
    ) : null}
  </div>
);

const dueStatePresentation = (dueRaw, isDarkMode) => {
  const s = String(dueRaw || '')
    .trim()
    .toLowerCase();
  const label = dueRaw && String(dueRaw).trim() ? String(dueRaw).trim() : '—';
  if (s === 'vencida') {
    return {
      label,
      bg: isDarkMode ? '#7f1d1d' : '#fee2e2',
      color: isDarkMode ? '#fca5a5' : '#991b1b',
      border: isDarkMode ? '#ef4444' : '#fecaca',
      accent: '#ef4444',
    };
  }
  if (s === 'por vencer' || s === 'pendiente') {
    return {
      label,
      bg: isDarkMode ? '#713f12' : '#fef9c3',
      color: isDarkMode ? '#fde68a' : '#854d0e',
      border: isDarkMode ? '#eab308' : '#fde68a',
      accent: '#eab308',
    };
  }
  return {
    label,
    bg: isDarkMode ? '#27272a' : '#f0ebe3',
    color: isDarkMode ? '#e4e4e7' : '#44403c',
    border: isDarkMode ? '#3f3f46' : '#d6d0c4',
    accent: isDarkMode ? '#52525b' : '#a8a29e',
  };
};

const TABLE_COLUMNS = [
  { key: 'Contacto', align: 'left', sortKey: 'contact_name' },
  { key: 'Empresa', align: 'left', sortKey: 'organization_name' },
  { key: 'Realizado por', align: 'left', sortKey: 'owner_name' },
  { key: 'Acción', align: 'left', sortKey: 'action' },
  { key: 'Próximo paso', align: 'left', sortKey: 'next_step' },
  { key: 'Estado', align: 'center', sortKey: 'due_state' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'contact_name':
      return String(row.contact_name || '').toLowerCase();
    case 'organization_name':
      return String(row.organization_name || '').toLowerCase();
    case 'owner_name':
      return String(row.owner_name || '').toLowerCase();
    case 'action':
      return String(row.action || '').toLowerCase();
    case 'next_step':
      return `${row.next_step_date || row.follow_up_reminder_date || ''} ${row.next_step || ''}`.toLowerCase();
    case 'due_state':
      return String(row.due_state || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const formatDateLabel = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [y, m, d] = raw.slice(0, 10).split('-');
    return `${d}-${m}-${y}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw.slice(0, 10);
  return parsed.toLocaleDateString('es-CL');
};

/**
 * Tabla de seguimiento comercial (próximos pasos): mismo look que Table3.
 */
const ProspectTrackingTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Seguimiento',
  subtitleOverride,
  onRowClick,
  hideChrome = false,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterDue, setFilterDue] = useState('');
  const [sortColumn, setSortColumn] = useState('due_state');
  const [sortDir, setSortDir] = useState('asc');

  const dueOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r.due_state || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.contact_name,
          ...(Array.isArray(row.contact_names) ? row.contact_names : []),
          row.organization_name,
          row.owner_name,
          row.action,
          row.next_step,
          row.next_step_detail,
          row.next_step_date,
          row.follow_up_reminder_date,
          row.due_state,
          row.response,
          row.notes,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterDue) {
      list = list.filter((row) => String(row.due_state || '').trim() === filterDue);
    }
    return list;
  }, [rows, searchQuery, filterDue]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      // Prefer overdue first when sorting by due_state asc
      if (sortColumn === 'due_state') {
        const rank = (row) =>
          String(row.due_state || '').toLowerCase() === 'vencida' ? 0 : 1;
        const diff = (rank(a) - rank(b)) * (sortDir === 'asc' ? 1 : -1);
        if (diff !== 0) return diff;
      }
      return compareRows(a, b, sortColumn, sortDir);
    });
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir(key === 'due_state' ? 'asc' : 'asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterDue('');
  };

  const hasActiveFilters = Boolean(searchQuery.trim()) || Boolean(filterDue);

  const subtitle =
    subtitleOverride != null
      ? subtitleOverride
      : loading
        ? 'Cargando…'
        : error ||
          (hasActiveFilters
            ? `Mostrando ${sortedRows.length} de ${rows.length} seguimientos`
            : `${rows.length} seguimientos pendientes`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const truncate = (value, max = 72) => {
    const s = String(value || '').trim();
    if (!s) return '—';
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  };

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
      </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Contacto, empresa, acción, próximo paso…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado seguimiento
          </span>
          <select value={filterDue} onChange={(e) => setFilterDue(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {dueOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '920px' }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: col.align === 'center' ? 'center' : 'flex-start',
                      gap: 4,
                      width: col.align === 'center' ? '100%' : 'auto',
                    }}
                  >
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando seguimientos…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay seguimientos pendientes para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const due = dueStatePresentation(row.due_state, isDarkMode);
                const nextDate = row.has_corrupt_dates
                  ? 'Fecha inválida en la base de datos'
                  : formatDateLabel(
                      row.next_step_date || row.follow_up_reminder_date || row.activity_date
                    );
                const nextLabel =
                  String(row.next_step || '').trim() ||
                  (row.has_follow_up_reminder || row.follow_up_reminder_date
                    ? 'Recordatorio de seguimiento'
                    : row.has_next_steps
                      ? 'Siguiente paso'
                      : '—');
                const contactLabel =
                  (Array.isArray(row.contact_names) && row.contact_names.length
                    ? row.contact_names.join(', ')
                    : row.contact_name) || '—';
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: bg,
                      cursor: onRowClick ? 'pointer' : 'default',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = bg;
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    <td style={{ padding: `${padV}px 0`, verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '36px' }}>
                        <div
                          style={{
                            width: '3px',
                            flexShrink: 0,
                            background: due.accent,
                            borderRadius: '0 2px 2px 0',
                          }}
                        />
                        <div style={{ padding: `2px ${padH + 2}px`, display: 'flex', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowClick?.(row);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              cursor: onRowClick ? 'pointer' : 'default',
                              textAlign: 'left',
                              fontWeight: 700,
                              fontSize: '12px',
                              color: '#2563eb',
                              lineHeight: 1.2,
                            }}
                          >
                            {truncate(contactLabel, 42)}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '220px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.25 }}>
                        {truncate(row.organization_name, 40)}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '160px' }}>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: row.owner_name ? text : muted,
                          lineHeight: 1.25,
                        }}
                      >
                        {row.owner_name || '—'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '160px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.25 }}>
                        {row.action || '—'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '260px' }}>
                      <Stacked
                        top={truncate(nextLabel, 48)}
                        bottom={nextDate || null}
                        topColor={text}
                        bottomColor={muted}
                        mutedColor={muted}
                      />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          minWidth: '90px',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '10px',
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          background: due.bg,
                          color: due.color,
                          border: `1px solid ${due.border}`,
                          lineHeight: 1.25,
                        }}
                      >
                        {due.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProspectTrackingTable3;
